import ShareStudy from '@vindr/workspace-core/modules/ShareStudy';
import { useGlobalLayoutState } from '#contexts/GlobalLayoutContext';
import OrderWorklistSidebar from '#pages/order-worklist/OrderWorklistSidebar';
import {
  WORKLIST_COLUMNS_ORIGINAL,
  WORKLIST_COLUMNS_SETTING,
} from '#pages/order-worklist/constants';
import { Editor } from '@tinymce/tinymce-react';
import { useGetEsignHistoryDetail } from '@vindr/services/actions/esign.action';
import {
  useGetOrderDetail,
  useGetOrderWorklistInterval,
  useMutationGetOrder,
} from '@vindr/services/actions/order.action';
import { useGetPrintPDF } from '@vindr/services/actions/print.action';
import {
  useGetLatestReportByOrder,
  useGetLatestReportFromOrder,
  useGetReportByVersionId,
} from '@vindr/services/actions/report.action';
import { openViewer } from '@vindr/services/actions/study.action';
import { ESIGN_STATUS } from '@vindr/services/apis/esign.api';
import {
  OrderWorklist,
  ParamsGetOrderWorklist,
  ResponseGetOrderDetail,
  ResponseGetOrderWorklist,
} from '@vindr/services/apis/order.api';
import { INIT_PAGINATION } from '@vindr/services/constants/common.constant';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { ORDER_STATUS } from '@vindr/services/constants/order.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import { MODALITIES } from '@vindr/services/constants/study.constant';
import { PaginationProps } from '@vindr/services/interfaces';
import keycloak from '@vindr/services/keycloak';
import { SidePanel, SiderLayout, Table } from '@vindr/ui';
import { extendDateOptions } from '@vindr/ui/src/components/ui/DateRangePicker';
import Loading from '@vindr/ui/src/components/ui/Loading';
import useDragResizeColumn from '@vindr/ui/src/hooks/useDragResizeColumn';
import { addToArray, filterValidValue, getArrayVal } from '@vindr/utils/helpers/common.helper';
import useWindowSize from '@vindr/utils/hooks/useWindowSize';
import onClickRow, { onDoubleClickRow } from '@vindr/utils/state/onClickRow';
import onPaginationChange from '@vindr/utils/state/onPaginationChange';
import type { SetState } from '@vindr/utils/types';
import { useGlobalState } from '@vindr/workspace-core/contexts/GlobalContext';
import AttachmentFile from '@vindr/workspace-core/modules/AttachmentFile';
import MatchOrder from '@vindr/workspace-core/modules/MatchOrder';
import EditOrder from '@vindr/workspace-core/modules/Order/EditOrder';
import Report from '@vindr/workspace-core/modules/Report';
import PrintModal from '@vindr/workspace-core/modules/Report/Print';
import useGenerateReport from '@vindr/workspace-core/modules/Report/useGenerateReport';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import {
  MouseEvent,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDragListView from 'react-drag-listview';
import { useTranslation } from 'react-i18next';
import { Updater, useImmer } from 'use-immer';
import Filter from './Filter';
import OrderWorklistHeader from './OrderWorklistHeader';
import useContextMenu, { MENU_TYPE } from './useContextMenu';
import useDefinedColumns from './useDefinedColumns';

export const INITIAL_SORTER_ORDER_WORKLIST: {
  sort_field: Parameters<typeof useGetOrderWorklistInterval>[0]['sort_field'];
  sort_order: Parameters<typeof useGetOrderWorklistInterval>[0]['sort_order'];
} = {
  sort_field: 'patient_arrived_time',
  sort_order: SORT_ORDER.DESC,
};

export const INITIAL_FILTER_ORDER_WORKLIST: ParamsGetOrderWorklist = {
  patient_arrived_time_from: extendDateOptions['Today'][0].toISOString(),
  patient_arrived_time_to: extendDateOptions['Today'][1].toISOString(),
};

const StudyList = () => {
  const { width } = useWindowSize();
  const [pagination, setPagination] = useImmer<PaginationProps>(INIT_PAGINATION);
  const [params, setParams] = useState(INITIAL_FILTER_ORDER_WORKLIST);
  const [sideFilter, setSideFilter] = useState({});
  const [sorter, setSorter] = useState(INITIAL_SORTER_ORDER_WORKLIST);
  const { iframe } = useGlobalLayoutState();
  const { modal, message } = useGlobalState();
  const [iframeState, setOpenIframe] = iframe;
  const [collapsedLeft, setCollapsedLeft] = useState(window.innerWidth < 640);
  const [collapsedRight, setCollapsedRight] = useState(window.innerWidth < 640);

  const response = useGetOrderWorklistInterval({
    offset: (pagination.page - 1) * pagination.size,
    limit: pagination.size,
    ...sorter,
    ...sideFilter,
    ...params,
  });
  const count = response?.data?.count || 0;
  const orders = response?.data?.data || [];
  const isLoading = response?.isLoading;
  const isFetching = response?.isFetching;
  const isRefetching = response?.isRefetching;
  const refetch = response?.refetch;

  const [isRefetch, setIsRefetch] = useState(false);
  useEffect(() => {
    if (!isFetching) {
      // After fetching, set isRefetch to false;
      setIsRefetch(false);
    }
  }, [isFetching]);

  const handleFilter = useCallback((p, type?: string) => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setParams((prev: any) => (prev?.filter_id ? { ...p, filter_id: prev.filter_id } : p));

    //? on mobile, not close when choose select multiple
    if (type === 'statuses') return;

    setCollapsedRight(true);
  }, []);

  const forceSearch = useCallback(
    (params) => {
      handleFilter(params);
      // Set refetch to true only when force search
      setIsRefetch(true);
      refetch?.();
    },
    [handleFilter, refetch]
  );

  const [selectedRowKeys, setSelectedRowKeys] = useState<typeof orders>([]);
  const [matchOrderState, setMatchOrderState] = useState<WorklistStates['matchOrderState']>({
    visible: false,
    study: null,
  });
  const [reportState, setReportState] = useState<WorklistStates['reportState']>({
    visible: false,
    study_id: null,
    order_id: null,
  });
  const [shareState, setShareState] = useState<WorklistStates['shareState']>({
    visible: false,
    study_id: null,
    visit_id: null,
  });
  const [attachmentState, setAttachmentState] = useState<WorklistStates['attachmentState']>({
    visible: false,
    order: null,
  });
  const [updateOrderState, setUpdateOrderState] = useState<WorklistStates['updateOrderState']>({
    visible: false,
    orderDetail: null,
  });

  const { data: orderDetailResponse } = useGetOrderDetail(attachmentState.order?.id, {
    enabled: !!attachmentState.order?.id,
  });
  const orderDetail = orderDetailResponse?.data;

  useLayoutEffect(() => {
    if (window.innerWidth < 640) {
      setCollapsedLeft(true);

      return;
    }
    setCollapsedLeft(iframeState.open);
  }, [iframeState]);

  const handleSearchSidebar = useCallback((val, type) => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    if (type === 'filter_id') {
      setSideFilter({ filter_id: val });
    } else if (type === 'institution_id') {
      setSideFilter({ institution_ids: [val] });
    } else {
      setSideFilter({});
    }
  }, []);

  // const debounceAlert = debounce(() => message.info('Match order first'), 500);

  const uploadParams: Parameters<typeof AttachmentFile>[0]['params'] = useMemo(
    () => ({
      accession_number: attachmentState.order?.order_group,
      device: orderDetail?.device_id,
      device_code: orderDetail?.device?.code,
      institution_id: attachmentState.order?.institution_id,
      modality: MODALITIES[13],
      patient_id: attachmentState.order?.patient_id,
      study_datetime: dayjs().toISOString(),
      ...filterValidValue(
        {
          'patient_name': attachmentState.order?.patient_name,
          'study_description': attachmentState.order?.study_desc,
          'study_instance_uid': attachmentState.order?.study_instance_uid,
          sex: attachmentState.order?.gender,
          visit_id: attachmentState.order?.visit_id,
          birth_date: attachmentState?.order?.patient_birth_date,
        },
        {
          invalidList: [null, undefined, ''],
        }
      ),
    }),
    [attachmentState, orderDetail]
  );

  const handleChangeLeftSidebar = useCallback((val: boolean) => {
    setCollapsedLeft(val);
    if (!val) setCollapsedRight(true);
  }, []);

  const handleChangeRightSidebar = useCallback((val) => {
    setCollapsedRight(val);
    if (!val) setCollapsedLeft(true);
  }, []);

  const handleCancelMatchOrder = useCallback(
    () => setMatchOrderState({ visible: false, study: null }),
    []
  );

  const handleCancelReport = useCallback(
    () => setReportState({ visible: false, study_id: null, order_id: null }),
    []
  );

  const handleChangeShareStudy = useCallback(
    () => setShareState({ visible: false, study_id: null, visit_id: null }),
    []
  );

  const handleChangeAttachmentFile = useCallback(
    () => setAttachmentState({ visible: false, order: {} }),
    []
  );

  const handleCloseUpdateOrder = useCallback(
    () => setUpdateOrderState({ visible: false, orderDetail: null }),
    []
  );

  return (
    <SiderLayout
      rightSidebar={
        width < 640 ? (
          <SidePanel
            collapsed={collapsedRight}
            side="right"
            width={215}
            onChange={handleChangeRightSidebar}
          >
            <div className="m-2">
              <Filter
                forceSearch={forceSearch}
                initFilters={INITIAL_FILTER_ORDER_WORKLIST}
                onChange={handleFilter}
              />
            </div>
          </SidePanel>
        ) : null
      }
      sidebar={
        <OrderWorklistSidebar
          collapsed={collapsedLeft}
          onChange={handleChangeLeftSidebar}
          onSearch={handleSearchSidebar}
        />
      }
    >
      <div className="flex h-full flex-col overflow-auto rounded-sm bg-background-light-3 p-4 dark:bg-background-dark-3">
        <OrderWorklistHeader selectedRowKeys={selectedRowKeys} totalItem={count} />
        {width >= 640 && (
          <Filter
            forceSearch={forceSearch}
            initFilters={INITIAL_FILTER_ORDER_WORKLIST}
            onChange={handleFilter}
          />
        )}
        <div className="flex h-0 flex-1 flex-col">
          <WorklistTable
            {...{
              count,
              isLoading,
              isRefetch,
              isRefetching,
              orders,
              pagination,
              selectedRowKeys,
              setAttachmentState,
              setMatchOrderState,
              setOpenIframe,
              setPagination,
              setReportState,
              setSelectedRowKeys,
              setShareState,
              setSorter,
              setUpdateOrderState,
            }}
          />
        </div>
        {matchOrderState.study ? (
          <MatchOrder
            message={message}
            study={matchOrderState.study}
            visible={matchOrderState.visible}
            onCancel={handleCancelMatchOrder}
          />
        ) : null}
        {reportState.study_id || reportState.order_id ? (
          <Report
            attachmentParams={uploadParams}
            iframe={iframe}
            message={message}
            modal={modal}
            order_id={reportState.order_id}
            study_id={reportState.study_id}
            visible={reportState.visible}
            onCancel={handleCancelReport}
            // onMissingOrder={() => {
            //   if (!matchOrderState.visible) {
            //     debounceAlert();
            //     setMatchOrderState({ visible: true, study: reportState.study });
            //   }
            // }}
          />
        ) : null}
        {shareState.study_id ? (
          <ShareStudy
            message={message}
            modal={modal}
            stateAction="create"
            study_id={shareState.study_id}
            visible={shareState.visible}
            visit_id={shareState.visit_id}
            onCancel={handleChangeShareStudy}
          />
        ) : null}
        {attachmentState.visible ? (
          <AttachmentFile
            message={message}
            modal={modal}
            open={attachmentState.visible}
            params={uploadParams}
            onCancel={handleChangeAttachmentFile}
          />
        ) : null}
        {updateOrderState.visible ? (
          <EditOrder
            isEditing={updateOrderState.visible}
            message={message}
            orderData={updateOrderState?.orderDetail}
            onFinish={handleCloseUpdateOrder}
          />
        ) : null}
      </div>
    </SiderLayout>
  );
};

type WorklistStates = {
  orders: ReturnType<typeof useGetOrderWorklistInterval>['data']['data'];
  attachmentState: {
    visible: boolean;
    order: WorklistStates['orders'][number] | Record<string, never>;
  };
  setAttachmentState: SetState<WorklistStates['attachmentState']>;
  updateOrderState: {
    visible: boolean;
    orderDetail: ResponseGetOrderDetail['data'];
  };
  setUpdateOrderState: SetState<WorklistStates['updateOrderState']>;
  selectedRowKeys: WorklistStates['orders'];
  setSelectedRowKeys: SetState<WorklistStates['orders']>;
  matchOrderState: { visible: boolean; study: null | ResponseGetOrderWorklist['data'][0] };
  setMatchOrderState: SetState<WorklistStates['matchOrderState']>;
  setOpenIframe: ReturnType<typeof useGlobalState>['iframe'][1];
  pagination: PaginationProps;
  setPagination: Updater<WorklistStates['pagination']>;
  reportState: {
    visible: boolean;
    study_id: null | WorklistStates['matchOrderState']['study']['study_id'];
    order_id: null | WorklistStates['matchOrderState']['study']['id'];
  };
  setReportState: SetState<WorklistStates['reportState']>;
  shareState: {
    visible: boolean;
    study_id: WorklistStates['matchOrderState']['study']['study_id'];
    visit_id: WorklistStates['matchOrderState']['study']['visit_id'];
  };
  setShareState: SetState<WorklistStates['shareState']>;
  sorter: typeof INITIAL_SORTER_ORDER_WORKLIST;
  setSorter: SetState<WorklistStates['sorter']>;
  count: ReturnType<typeof useGetOrderWorklistInterval>['data']['count'];
  isLoading: ReturnType<typeof useGetOrderWorklistInterval>['isLoading'];
  isRefetch: ReturnType<typeof useGetOrderWorklistInterval>['isFetching'];
  isRefetching: ReturnType<typeof useGetOrderWorklistInterval>['isRefetching'];
};

const WorklistTable = memo(
  ({
    count,
    isLoading,
    isRefetch,
    isRefetching,
    orders,
    pagination,
    selectedRowKeys,
    setAttachmentState,
    setMatchOrderState,
    setOpenIframe,
    setPagination,
    setReportState,
    setSelectedRowKeys,
    setShareState,
    setSorter,
    setUpdateOrderState,
  }: Pick<
    WorklistStates,
    | 'count'
    | 'isLoading'
    | 'isRefetch'
    | 'isRefetching'
    | 'orders'
    | 'pagination'
    | 'selectedRowKeys'
    | 'setAttachmentState'
    | 'setMatchOrderState'
    | 'setOpenIframe'
    | 'setPagination'
    | 'setReportState'
    | 'setSelectedRowKeys'
    | 'setShareState'
    | 'setSorter'
    | 'setUpdateOrderState'
  >) => {
    const performedColumns = useDefinedColumns(pagination);

    const {
      components,
      dragProps,
      getDefaultCol,
      resetColumns,
      resizableColumns,
      setVisibleColumns,
      tableWidth,
      visibleColumns,
    } = useDragResizeColumn(performedColumns, WORKLIST_COLUMNS_SETTING, WORKLIST_COLUMNS_ORIGINAL);

    const { message } = useGlobalState();
    const { t } = useTranslation('Vindoc');
    const [print, setPrint] = useImmer<{ enabled: boolean; record: typeof orders[0] }>({
      enabled: false,
      record: null,
    });

    const openReport = useCallback((record: ResponseGetOrderWorklist['data'][0]) => {
      if (
        record?.status === ORDER_STATUS.CANCEL ||
        !keycloak.hasOneOfPerm([PERMISSIONS.worklist.report])
      )
        return;

      setReportState({ visible: true, study_id: record?.study_id, order_id: record?.id });
    }, []);
    const { mutate: getOrderDetail } = useMutationGetOrder();

    const handleContextMenuSelect = useCallback(
      (type, record: ResponseGetOrderWorklist['data'][0]) => {
        const noStudyMsg = 'No matched study found';

        function handleOpenReport() {
          openReport(record);
          setAttachmentState((prev) => ({ ...prev, order: record })); //? Pass order for call API, using upload Attachment inside Report
        }

        switch (type) {
          case MENU_TYPE.ORDER_MATCH:
            setMatchOrderState({ visible: true, study: record });
            break;
          case MENU_TYPE.OPEN_VIEWER:
            if (!record?.study_instance_uid) {
              message.error(t(noStudyMsg));
              return;
            }

            openViewer(record?.study_instance_uid, record?.modality);
            break;
          case MENU_TYPE.OPEN_VIEWER_AND_REPORT:
            handleOpenReport();

            if (!record?.study_instance_uid) {
              message.error(t(noStudyMsg));
              return;
            }

            openViewer(record?.study_instance_uid, record?.modality);
            break;
          case MENU_TYPE.OPEN_SPLIT_WINDOW:
            if (!record?.study_instance_uid) {
              message.error(t(noStudyMsg));
              return;
            }

            setOpenIframe(record.study_instance_uid, record?.modality);
            break;
          case MENU_TYPE.OPEN_SPLIT_WINDOW_AND_REPORT:
            handleOpenReport();

            if (!record?.study_instance_uid) {
              message.error(t(noStudyMsg));
              return;
            }

            setOpenIframe(record.study_instance_uid, record?.modality);
            break;
          case MENU_TYPE.OPEN_REPORT:
            handleOpenReport();
            break;
          case MENU_TYPE.PRINT:
            setPrint((draft) => {
              draft.enabled = true;
              draft.record = record;
            });
            break;
          case MENU_TYPE.SHARE_STUDY:
            setShareState({
              visible: true,
              study_id: record?.study_id,
              visit_id: record?.visit_id,
            });
            break;
          case MENU_TYPE.ATTACHMENT_FILE:
            setAttachmentState({ visible: true, order: record });
            break;
          case MENU_TYPE.UPDATE_ORDER: {
            getOrderDetail(record?.id, {
              onSuccess(data) {
                setUpdateOrderState({ visible: true, orderDetail: data?.data });
              },
            });
            break;
          }
          default:
            break;
        }
      },
      [
        getOrderDetail,
        message,
        openReport,
        setAttachmentState,
        setMatchOrderState,
        setOpenIframe,
        setPrint,
        setShareState,
        setUpdateOrderState,
        t,
      ]
    );

    const contextMenuItems = useContextMenu({ handleContextMenuSelect });

    const onClosePrint = useCallback(() => {
      setPrint((draft) => {
        draft.enabled = false;
        draft.record = null;
      });
    }, [setPrint]);

    return (
      <>
        <ReactDragListView.DragColumn {...dragProps}>
          <Table<typeof orders[0]>
            columns={resizableColumns}
            components={components}
            count={count}
            dataSource={orders}
            loading={isLoading || isRefetch || isEmpty(resizableColumns)}
            pagination={pagination}
            rowKey="id"
            scroll={{ x: tableWidth }}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            wrapperClassName="min-h-[210px]"
            contextMenu={{
              items: contextMenuItems,
              onClick: (menuItem, record) => {
                if (menuItem.onClick) {
                  menuItem.onClick(record);
                }
              },
            }}
            rowCustom={{
              getDefaultCol,
              performedColumns,
              resetColumns,
              setVisibleColumns,
              settingKey: WORKLIST_COLUMNS_SETTING,
              visibleColumns,
            }}
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: selectedRowKeys.map((x) => x.id),
              onChange: (keys, records: typeof orders) => {
                setSelectedRowKeys(records);
              },
            }}
            onPaginationChange={(p) => {
              setPagination((draft) => onPaginationChange(draft, p));
            }}
            onRow={(record) => {
              return {
                onClick: (event: MouseEvent) => onClickRow({ event, setSelectedRowKeys, record }),
                onDoubleClick: (event) =>
                  onDoubleClickRow({
                    event,
                    callback: () => contextMenuItems[0].onClick(record),
                  }),
              };
            }}
            onTableChange={(_, __, sorter) =>
              setSorter({
                sort_field: sorter.field,
                sort_order: sorter.order === 'ascend' ? 0 : 1,
              })
            }
          />
        </ReactDragListView.DragColumn>
        {print.enabled && (
          <PrintHandler enabled={print.enabled} record={print.record} onClose={onClosePrint} />
        )}
      </>
    );
  }
);
WorklistTable.displayName = 'WorklistTable';

export default StudyList;

const PrintHandler = memo(
  ({
    record,
    onClose,
    enabled,
  }: {
    record: OrderWorklist;
    onClose: () => void;
    enabled: boolean;
  }) => {
    const { message } = useGlobalState();
    const { t } = useTranslation('Vindoc');
    const { data: latestReportResponse } = useGetLatestReportByOrder(record?.id, {
      enabled: enabled && !!record?.id,
    });
    const latestReport = useMemo(() => latestReportResponse?.data, [latestReportResponse?.data]);
    const { data: reportDetailResponse } = useGetReportByVersionId(
      latestReport?.report_version?.id,
      { enabled: enabled && !!latestReport?.report_version?.id }
    );
    const reportDetail = useMemo(
      () => getArrayVal(reportDetailResponse?.data),
      [reportDetailResponse?.data]
    );
    const isSignedStatus = useMemo(
      () =>
        [
          ESIGN_STATUS['Approver Signed'],
          ESIGN_STATUS['Submitter Signed'],
          ESIGN_STATUS['Approver Failed'],
          ESIGN_STATUS['Revoke Failed'],
        ].includes(reportDetail?.esign_order?.status),
      [reportDetail?.esign_order?.status]
    );
    const { data: PDFReportResponse } = useGetEsignHistoryDetail(reportDetail?.esign_order?.id, {
      enabled: enabled && !!isSignedStatus,
    });
    const signed_report = useMemo(
      () => PDFReportResponse?.data?.signed_report,
      [PDFReportResponse?.data?.signed_report]
    );
    const isSignedReport = isSignedStatus && signed_report;
    const { data, isError, isLoading } = useGetLatestReportFromOrder(record, {
      enabled: enabled && !isSignedReport,
    });

    const {
      data: PDFUrl,
      isLoading: isLoadingPDFUrl,
      isFetching: isFetchingPDFUrl,
      isInitialLoading: isInitialLoadingPDFUrl,
      isError: isPDFError,
    } = useGetPrintPDF(signed_report, {
      enabled: enabled && !!signed_report,
    });

    const reports = useMemo(() => addToArray(data), [data]) as typeof data[];
    const isQueriesLoaded = useMemo(() => !isLoading, [isLoading]);

    const [isReportLoading, setIsReportLoading] = useState(enabled);

    const printRef = useRef<Editor>(null);
    const { handlePrint, isLoading: isPrinting } = useGenerateReport({ reports });

    useEffect(() => {
      if (!enabled) return;
      if (!(printRef.current && isQueriesLoaded)) return;
      if (isSignedReport && (isLoadingPDFUrl || isInitialLoadingPDFUrl || isFetchingPDFUrl)) return;
      setIsReportLoading(true);

      if (isError || isPDFError) {
        setIsReportLoading(false);
        onClose();
        return message.error({ content: t(MESSAGE.ERROR.EMSG_0001) });
      }

      if (isSignedReport) {
        setIsReportLoading(false);
        handlePrint(PDFUrl, onClose);
        return onClose();
      }

      setTimeout(() => {
        setIsReportLoading(false);
        //? 2nd setTimeout for wait modal Loading closed then open Print
        return setTimeout(() => {
          handlePrint(printRef.current?.editor?.iframeElement, onClose);
          onClose();
        }, 0);
      }, 2 * 1000);
    }, [
      enabled,
      handlePrint,
      isError,
      isInitialLoadingPDFUrl,
      isQueriesLoaded,
      isSignedReport,
      message,
      onClose,
      PDFUrl,
      t,
      isPDFError,
      isFetchingPDFUrl,
      isLoadingPDFUrl,
    ]);

    return (
      <>
        <PrintModal
          ref={printRef}
          handleClose={() => onClose()}
          open={true}
          reports={reports}
          zIndex={-1}
        />
        {(isReportLoading || isPrinting) && (
          <div className="fixed top-0 left-0 right-0 bottom-0 z-[1500] flex items-center justify-center bg-[#00000073]">
            <Loading size="large" />
          </div>
        )}
      </>
    );
  },
  (prev, next) => isEqual(prev.record, next.record) && prev.enabled === next.enabled
);
PrintHandler.displayName = 'PrintHandler';
